import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { rewardApi } from '../../api/reward.api';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import {
  Gift,
  BarChart3,
  Settings,
  ArrowLeftRight,
  Users,
  Coins,
  TrendingUp,
  Microscope,
  Tag,
  CalendarDays,
} from 'lucide-react';
import { RewardConfigPanel } from './RewardConfigPanel';
import { RewardTypeLabelPanel } from './RewardTypeLabelPanel';
import { RewardStatsChart } from './RewardStatsChart';
import { CreatorRewardConfigPanel } from './CreatorRewardConfigPanel';
import { PieceEconomyDashboard } from './PieceEconomyDashboard';
import { ProfitabilityDashboard } from './ProfitabilityDashboard';
import { AnalysisConfigPanel } from './AnalysisConfigPanel';
import { RewardHistoryPanel } from './history/RewardHistoryPanel';
import { ExchangeHistoryPanel } from './history/ExchangeHistoryPanel';
import { DailyPiecePanel } from './history/DailyPiecePanel';

export const RewardManagement = () => {
  /** States */
  const [activeSubTab, setActiveSubTab] = useState<
    | 'overview'
    | 'daily-piece'
    | 'piece'
    | 'config'
    | 'stats'
    | 'exchange'
    | 'creator'
    | 'profitability'
    | 'analysis'
    | 'labels'
  >('overview');

  /** APIs */
  const { data: summary, isLoading: summaryLoading } = useQuery({
    queryKey: ['adminRewardSummary'],
    queryFn: () => rewardApi.getSummary().then((res) => res.data.results),
  });

  /** 금액 포맷팅 */
  const formatAmount = (amount: number) => {
    return amount.toLocaleString('ko-KR') + '원';
  };

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <h2 className="text-lg sm:text-xl font-bold text-gray-900 flex items-center gap-2">
          <Gift className="w-5 h-5" />
          리워드 관리
        </h2>
      </div>

      {/* 서브탭 */}
      <div className="flex flex-wrap gap-2">
        <Button
          variant={activeSubTab === 'overview' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveSubTab('overview')}
        >
          현황
        </Button>
        <Button
          variant={activeSubTab === 'daily-piece' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveSubTab('daily-piece')}
          className="flex items-center gap-1"
        >
          <CalendarDays className="w-3 h-3" />
          일별 조각수령
        </Button>
        <Button
          variant={activeSubTab === 'piece' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveSubTab('piece')}
          className="flex items-center gap-1"
        >
          <Coins className="w-3 h-3" />
          조각 경제
        </Button>
        <Button
          variant={activeSubTab === 'config' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveSubTab('config')}
          className="flex items-center gap-1"
        >
          <Settings className="w-3 h-3" />
          설정
        </Button>
        <Button
          variant={activeSubTab === 'stats' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveSubTab('stats')}
          className="flex items-center gap-1"
        >
          <BarChart3 className="w-3 h-3" />
          통계
        </Button>
        <Button
          variant={activeSubTab === 'exchange' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveSubTab('exchange')}
          className="flex items-center gap-1"
        >
          <ArrowLeftRight className="w-3 h-3" />
          토스 포인트 지급
        </Button>
        <Button
          variant={activeSubTab === 'creator' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveSubTab('creator')}
          className="flex items-center gap-1"
        >
          <Users className="w-3 h-3" />
          작성자 설정
        </Button>
        <Button
          variant={activeSubTab === 'profitability' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveSubTab('profitability')}
          className="flex items-center gap-1"
        >
          <TrendingUp className="w-3 h-3" />
          수익성
        </Button>
        <Button
          variant={activeSubTab === 'analysis' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveSubTab('analysis')}
          className="flex items-center gap-1"
        >
          <Microscope className="w-3 h-3" />
          분석 정책
        </Button>
        <Button
          variant={activeSubTab === 'labels' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveSubTab('labels')}
          className="flex items-center gap-1"
        >
          <Tag className="w-3 h-3" />
          라벨
        </Button>
      </div>

      {/* 서브탭 컨텐츠 */}
      {activeSubTab === 'daily-piece' ? (
        <DailyPiecePanel />
      ) : activeSubTab === 'labels' ? (
        <RewardTypeLabelPanel />
      ) : activeSubTab === 'analysis' ? (
        <AnalysisConfigPanel />
      ) : activeSubTab === 'profitability' ? (
        <ProfitabilityDashboard />
      ) : activeSubTab === 'piece' ? (
        <PieceEconomyDashboard />
      ) : activeSubTab === 'config' ? (
        <RewardConfigPanel />
      ) : activeSubTab === 'stats' ? (
        <RewardStatsChart />
      ) : activeSubTab === 'exchange' ? (
        <ExchangeHistoryPanel />
      ) : activeSubTab === 'creator' ? (
        <CreatorRewardConfigPanel />
      ) : (
        <>
          {/* 요약 카드 4개 */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <div className="p-5">
                <p className="text-xs text-gray-500 mb-1">누적 지급액</p>
                <p className="text-xl font-bold text-gray-900">
                  {summaryLoading ? '...' : formatAmount(summary?.totalPaidAmount ?? 0)}
                </p>
              </div>
            </Card>
            <Card>
              <div className="p-5">
                <p className="text-xs text-gray-500 mb-1">오늘 지급액</p>
                <p className="text-xl font-bold text-blue-600">
                  {summaryLoading ? '...' : formatAmount(summary?.todayPaidAmount ?? 0)}
                </p>
              </div>
            </Card>
            <Card>
              <div className="p-5">
                <p className="text-xs text-gray-500 mb-1">오늘 건수</p>
                <p className="text-xl font-bold text-green-600">
                  {summaryLoading ? '...' : `${summary?.todayPaidCount ?? 0}건`}
                </p>
              </div>
            </Card>
            <Card>
              <div className="p-5">
                <p className="text-xs text-gray-500 mb-1">오늘 활성 유저</p>
                <p className="text-xl font-bold text-purple-600">
                  {summaryLoading ? '...' : `${summary?.activeUsers ?? 0}명`}
                </p>
              </div>
            </Card>
          </div>

          {/* 히스토리 테이블 */}
          <RewardHistoryPanel />
        </>
      )}
    </div>
  );
};
